import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import renderWeatherIcon from "../Other/GetWeatherIcon";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// Map the user's branch label to a weather branch (mirrors HeaderWeather).
const branchForUser = (branch) => {
  switch (branch) {
    case "Corporate":
      return "Granger";
    case "Window/Door - Mishawaka":
      return "Mishawaka";
    case "Mill - Fort Wayne":
      return "Fort Wayne";
    case null:
    case undefined:
      return "Granger";
    default:
      return branch;
  }
};

// Compact current-conditions tile for the homepage hero.
const HomeWeatherWidget = () => {
  const user = useSelector(selectUser);
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const branch = branchForUser(user?.branch);

  const getWeather = useCallback(async () => {
    try {
      const res = await fetch(`/api/weather/getWeatherData?branch=${branch}`);
      const data = await res.json();
      const record = Array.isArray(data) ? data[0] : null;
      if (record && record.current) {
        setWeather(record);
        setStatus("ok");
      } else {
        setStatus((prev) => (prev === "ok" ? "ok" : "error"));
      }
    } catch {
      setStatus((prev) => (prev === "ok" ? "ok" : "error"));
    }
  }, [branch]);

  useEffect(() => {
    if (user) getWeather();
  }, [user, getWeather]);

  // Inset tile on the homepage hero; follows all four themes via the
  // app-surface tokens.
  const shell =
    "app-surface flex min-h-[4.75rem] min-w-[13rem] items-center gap-3 rounded-2xl border px-4 py-3";

  if (status === "loading") {
    return (
      <div className={shell} aria-busy="true">
        <span className="app-skeleton h-11 w-11 shrink-0 rounded-full" />
        <span className="flex-1 space-y-2">
          <span className="app-skeleton block h-5 w-16" />
          <span className="app-skeleton block h-3 w-28" />
        </span>
        <span className="sr-only">Loading weather…</span>
      </div>
    );
  }

  if (status === "error" || !weather) {
    return (
      <div className={shell}>
        <span className="app-icon-tile h-10 w-10 rounded-xl bg-slate-500/10 text-slate-500 dark:text-slate-400">
          <CloudOffRoundedIcon sx={{ fontSize: 20 }} />
        </span>
        <span className="app-muted text-sm">Weather unavailable</span>
      </div>
    );
  }

  const current = weather.current;

  return (
    <Link
      to="/dashboard/weather"
      className={`${shell} group transition hover:-translate-y-0.5 hover:border-sky-400/50`}
    >
      {/* The animated icons carry a lot of built-in padding, so the box is
          sized (and pulled in) to show the artwork at a useful size. */}
      <span className="-my-4 -ml-3 block h-20 w-20 shrink-0 [&>svg]:h-full [&>svg]:w-full">
        {renderWeatherIcon(
          current.weather?.[0]?.main,
          44,
          current.weather?.[0]?.description
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-3xl font-semibold leading-none tabular-nums tracking-tight">
          {Math.round(current.temp)}&deg;F
        </p>
        <p className="app-muted mt-1.5 truncate text-xs font-medium">
          {cap(weather.branch)} · {cap(current.weather?.[0]?.description)}
        </p>
      </div>
      <ChevronRightRoundedIcon
        className="app-muted shrink-0 transition group-hover:translate-x-0.5 group-hover:text-[color:var(--app-accent)]"
        sx={{ fontSize: 20 }}
      />
    </Link>
  );
};

export default HomeWeatherWidget;
